/**
 * DietPlannerWizard.js
 *
 * Top-level orchestrator for the AI Diet Planner flow, mounted at
 * src/components/sections/WelcomeScreen/DietPlannerWizard.js — a sibling of
 * the Welcome/ and Assesment/ folders.
 *
 * Owns the session id, biometrics, and current-step state, and renders
 * whichever screen is active. All transitions are client-side state swaps
 * — no route change, no extra page load.
 *
 * Flow: Welcome (1) → BasicInfo (2) → Goal (3) → Lifestyle (4) →
 * Activity Level (5) → Medical Information (6) → Food Preferences (7) →
 * Likes (8) → Dislikes (9) → Meal Schedule (10) → Women's Health (11,
 * female only, per FSD Section 5 Conditional Display Logic) → Summary (12)
 * → Loading Screen → Diet Plan Screen (not built yet).
 * Every user reaches Summary now — non-female users skip straight there
 * from Meal Schedule. Tapping "Generate Diet Plan" on Summary navigates to
 * the Loading Screen, which owns the actual generation call (UC-02 steps
 * 2-7) and hands the finished plan back via onSuccess.
 */

import React, { useState, useCallback } from "react";
import Welcome from "../Welcome/Welcome";
import BasicInfoStep from "../BasicInfo/BasicInfoStep";
import GoalStep from "../Assesment/GoalStep";
import LifestyleStep from "../Lifestyle/LifestyleStep";
import ActivityLevelStep from "../ActivityLevel/ActivityLevelStep";
import MedicalInfoStep from "../MedicalInfo/MedicalInfoStep";
import FoodPreferencesStep from "../FoodPreference/FoodPreferencesStep";
import LikesStep from "../Like/LikesStep";
import DislikesStep from "../Dislike/DislikesStep";
import MealScheduleStep from "../Meal/MealScheduleStep";
import WomensHealthStep from "../WomenHealth/WomensHealthStep";
import SummaryStep from "../SummaryScreen/SummaryStep";
import LoadingScreen from "../Loading/LoadingScreen";
import DietPlanScreen from "../DietPlan/DietPlanScreen";
import { buildMealContext } from "../MealEngine/MealEngine";
import EmailPopup from "../DietPlan/EmailPopup";
import { STEP_NUMBERS } from "../Assesment/assessmentConfig";

const SCREENS = {
  WELCOME: "welcome",
  BASIC_INFO: "basicInfo",
  GOAL: "goal",
  LIFESTYLE: "lifestyle",
  ACTIVITY: "activity",
  MEDICAL: "medical",
  FOOD_PREFERENCES: "foodPreferences",
  LIKES: "likes",
  DISLIKES: "dislikes",
  MEAL_SCHEDULE: "mealSchedule",
  WOMENS_HEALTH: "womensHealth",
  SUMMARY: "summary",
  LOADING: "loading",
  DIET_PLAN: "dietPlan",
};

// Lets SummaryStep's Edit buttons speak in step numbers (the canonical
// source of truth from assessmentConfig.js) without needing to know this
// wizard's internal SCREENS string keys.
const STEP_TO_SCREEN = {
  [STEP_NUMBERS.BASIC_INFO]: SCREENS.BASIC_INFO,
  [STEP_NUMBERS.GOAL]: SCREENS.GOAL,
  [STEP_NUMBERS.LIFESTYLE]: SCREENS.LIFESTYLE,
  [STEP_NUMBERS.ACTIVITY]: SCREENS.ACTIVITY,
  [STEP_NUMBERS.MEDICAL]: SCREENS.MEDICAL,
  [STEP_NUMBERS.FOOD_PREFERENCES]: SCREENS.FOOD_PREFERENCES,
  [STEP_NUMBERS.LIKES]: SCREENS.LIKES,
  [STEP_NUMBERS.DISLIKES]: SCREENS.DISLIKES,
  [STEP_NUMBERS.MEAL_SCHEDULE]: SCREENS.MEAL_SCHEDULE,
  [STEP_NUMBERS.WOMENS_HEALTH]: SCREENS.WOMENS_HEALTH,
};

const DietPlannerWizard = () => {
  const [screen, setScreen] = useState(SCREENS.WELCOME);
  const [sessionId, setSessionId] = useState(null);

  // ── Lifted assessment data — grows as more screens are built ────────────────
  const [basicInfo, setBasicInfo] = useState(null); // { name, age, gender, heightCm, weightKg, targetWeightKg }
  const [goal, setGoal] = useState(null);
  const [lifestyle, setLifestyle] = useState(null);
  const [activity, setActivity] = useState(null);
  const [medical, setMedical] = useState(null);
  const [foodPreferences, setFoodPreferences] = useState(null);
  const [likes, setLikes] = useState(null);
  const [dislikes, setDislikes] = useState(null);
  const [mealSchedule, setMealSchedule] = useState(null);
  const [womensHealth, setWomensHealth] = useState(null);
  const [dietPlan, setDietPlan] = useState(null); // set once LoadingScreen's generation resolves
  const [showEmailPopup, setShowEmailPopup] = useState(false); // UC-06, opened from the Diet Plan Screen

  // Known as soon as BasicInfoStep's gender pill is tapped — well before
  // Next is pressed — so Screen 11 can be gated in/out here.
  const [isFemale, setIsFemale] = useState(false);

  // ── Welcome → Basic Info ─────────────────────────────────────────────────────
  const handleWelcomeNext = useCallback((sid) => {
    setSessionId(sid);
    setScreen(SCREENS.BASIC_INFO);
  }, []);

  // ── Basic Info navigation ─────────────────────────────────────────────────────
  const handleBasicInfoPrevious = useCallback(() => {
    setScreen(SCREENS.WELCOME);
  }, []);

  const handleGenderChange = useCallback((gender) => {
    setIsFemale(gender === "female");
  }, []);

  const handleBasicInfoNext = useCallback((data) => {
    setBasicInfo(data);
    setScreen(SCREENS.GOAL);
  }, []);

  // ── Goal step navigation ─────────────────────────────────────────────────────
  const handleGoalPrevious = useCallback(() => {
    setScreen(SCREENS.BASIC_INFO);
  }, []);

  const handleGoalNext = useCallback((selectedGoal) => {
    setGoal(selectedGoal);
    setScreen(SCREENS.LIFESTYLE);
  }, []);

  // ── Lifestyle step navigation ────────────────────────────────────────────────
  const handleLifestylePrevious = useCallback(() => {
    setScreen(SCREENS.GOAL);
  }, []);

  const handleLifestyleNext = useCallback((lifestyleData) => {
    setLifestyle(lifestyleData);
    setScreen(SCREENS.ACTIVITY);
  }, []);

  // ── Activity Level step navigation ───────────────────────────────────────────
  const handleActivityPrevious = useCallback(() => {
    setScreen(SCREENS.LIFESTYLE);
  }, []);

  const handleActivityNext = useCallback((activityData) => {
    setActivity(activityData);
    setScreen(SCREENS.MEDICAL);
  }, []);

  // ── Medical Information step navigation ──────────────────────────────────────
  const handleMedicalPrevious = useCallback(() => {
    setScreen(SCREENS.ACTIVITY);
  }, []);

  const handleMedicalNext = useCallback((medicalData) => {
    setMedical(medicalData);
    setScreen(SCREENS.FOOD_PREFERENCES);
  }, []);

  // ── Food Preferences step navigation ──────────────────────────────────────────
  const handleFoodPreferencesPrevious = useCallback(() => {
    setScreen(SCREENS.MEDICAL);
  }, []);

  const handleFoodPreferencesNext = useCallback((foodPreferencesData) => {
    console.log('handleFoodPreferencesNext Diet Planner Wizard',foodPreferencesData)
    setFoodPreferences(foodPreferencesData);
    setScreen(SCREENS.LIKES);
  }, []);

  // ── Likes step navigation ────────────────────────────────────────────────────
  const handleLikesPrevious = useCallback(() => {
    setScreen(SCREENS.FOOD_PREFERENCES);
  }, []);

  const handleLikesNext = useCallback((likesData) => {
    setLikes(likesData);
    setScreen(SCREENS.DISLIKES);
  }, []);

  // ── Dislikes step navigation ─────────────────────────────────────────────────
  const handleDislikesPrevious = useCallback(() => {
    setScreen(SCREENS.LIKES);
  }, []);

  const handleDislikesNext = useCallback((dislikesData) => {
    setDislikes(dislikesData);
    setScreen(SCREENS.MEAL_SCHEDULE);
  }, []);

  // ── Meal Schedule step navigation ────────────────────────────────────────────
  const handleMealSchedulePrevious = useCallback(() => {
    setScreen(SCREENS.DISLIKES);
  }, []);

  const handleMealScheduleNext = useCallback(
    (mealScheduleData) => {
      setMealSchedule(mealScheduleData);

      // FSD Section 5, Conditional Display Logic: Screen 11 (Women's
      // Health) is skipped entirely — not shown, not counted in the
      // progress bar's *visited* count — when gender ≠ Female.
      setScreen(isFemale ? SCREENS.WOMENS_HEALTH : SCREENS.SUMMARY);
    },
    [isFemale]
  );

  // ── Women's Health step navigation (female only) ─────────────────────────────
  const handleWomensHealthPrevious = useCallback(() => {
    setScreen(SCREENS.MEAL_SCHEDULE);
  }, []);

  const handleWomensHealthNext = useCallback((womensHealthData) => {
    setWomensHealth(womensHealthData);
    setScreen(SCREENS.SUMMARY);
  }, []);

  // ── Summary step navigation ──────────────────────────────────────────────────
  const handleSummaryPrevious = useCallback(() => {
    // Return to whichever screen actually precedes Summary for this user.
    setScreen(isFemale ? SCREENS.WOMENS_HEALTH : SCREENS.MEAL_SCHEDULE);
  }, [isFemale]);

  const handleNavigateToStep = useCallback((stepNumber) => {
    const target = STEP_TO_SCREEN[stepNumber];
    if (target) setScreen(target);
  }, []);

  // Per UC-02 Main Flow step 1-2: "User clicks 'Generate Diet Plan' ...
  // System validates completeness ... navigates to Loading Screen."
  // SummaryStep has already gated this button on completeness
  // (nextDisabled), so by the time this fires we just navigate — the
  // actual generation call (steps 3-6) lives inside LoadingScreen.
  const handleGeneratePlan = useCallback(() => {
    setScreen(SCREENS.LOADING);
  }, []);

  // ── Loading Screen → Diet Plan Screen ────────────────────────────────────────
  const handleGenerationSuccess = useCallback((plan) => {
    setDietPlan(plan);
    setScreen(SCREENS.DIET_PLAN);
  }, []);

  // ── Diet Plan Screen → Email Popup (UC-06) ───────────────────────────────────
  const handleOpenEmailPopup = useCallback(() => {
    setShowEmailPopup(true);
  }, []);

  const handleCloseEmailPopup = useCallback(() => {
    setShowEmailPopup(false);
  }, []);

  const loadingPayload = {
    basicInfo,
    goal,
    lifestyle,
    activity,
    medical,
    foodPreferences,
    likes,
    dislikes,
    mealSchedule,
    womensHealth,
    isFemale,
  };

  // Same shape LoadingScreen's mock plan generation filters/ranks
  // against — computed here too so DietPlanScreen's regenerate/replace
  // mocks (UC-03/UC-04) can pick from the same diet-type/gluten/dislike-
  // eligible candidates as the original plan, instead of ignoring the
  // user's profile once a meal gets swapped.
  const mealContext = buildMealContext(loadingPayload);

  switch (screen) {
    case SCREENS.BASIC_INFO:
      return (
        <BasicInfoStep
          sessionId={sessionId}
          initialValues={basicInfo ?? {}}
          onPrevious={handleBasicInfoPrevious}
          onNext={handleBasicInfoNext}
          onGenderChange={handleGenderChange}
        />
      );

    case SCREENS.GOAL:
      return (
        <GoalStep
          sessionId={sessionId}
          initialGoal={goal}
          weightKg={basicInfo?.weightKg ?? null}
          targetWeightKg={basicInfo?.targetWeightKg ?? null}
          onPrevious={handleGoalPrevious}
          onNext={handleGoalNext}
        />
      );

    case SCREENS.LIFESTYLE:
      return (
        <LifestyleStep
          sessionId={sessionId}
          initialData={lifestyle}
          onPrevious={handleLifestylePrevious}
          onNext={handleLifestyleNext}
        />
      );

    case SCREENS.ACTIVITY:
      return (
        <ActivityLevelStep
          sessionId={sessionId}
          initialData={activity}
          onPrevious={handleActivityPrevious}
          onNext={handleActivityNext}
        />
      );

    case SCREENS.MEDICAL:
      return (
        <MedicalInfoStep
          sessionId={sessionId}
          initialData={medical}
          onPrevious={handleMedicalPrevious}
          onNext={handleMedicalNext}
        />
      );

    case SCREENS.FOOD_PREFERENCES:
      return (
        <FoodPreferencesStep
          sessionId={sessionId}
          initialData={foodPreferences}
          onPrevious={handleFoodPreferencesPrevious}
          onNext={handleFoodPreferencesNext}
        />
      );

    case SCREENS.LIKES:
      return (
        <LikesStep
          sessionId={sessionId}
          initialData={likes}
          foodPreferences={foodPreferences}
          onPrevious={handleLikesPrevious}
          onNext={handleLikesNext}
        />
      );

    case SCREENS.DISLIKES:
      return (
        <DislikesStep
          sessionId={sessionId}
          initialData={dislikes}
          foodPreferences={foodPreferences}
          likes={likes}
          onPrevious={handleDislikesPrevious}
          onNext={handleDislikesNext}
        />
      );

    case SCREENS.MEAL_SCHEDULE:
      return (
        <MealScheduleStep
          sessionId={sessionId}
          initialData={mealSchedule}
          onPrevious={handleMealSchedulePrevious}
          onNext={handleMealScheduleNext}
        />
      );

    case SCREENS.WOMENS_HEALTH:
      return (
        <WomensHealthStep
          sessionId={sessionId}
          initialData={womensHealth}
          onPrevious={handleWomensHealthPrevious}
          onNext={handleWomensHealthNext}
        />
      );

    case SCREENS.SUMMARY:
      return (
        <SummaryStep
          sessionId={sessionId}
          basicInfo={basicInfo}
          goal={goal}
          lifestyle={lifestyle}
          activity={activity}
          medical={medical}
          foodPreferences={foodPreferences}
          likes={likes}
          dislikes={dislikes}
          mealSchedule={mealSchedule}
          womensHealth={womensHealth}
          isFemale={isFemale}
          onPrevious={handleSummaryPrevious}
          onNext={handleGeneratePlan}
          onNavigateToStep={handleNavigateToStep}
        />
      );

    case SCREENS.LOADING:
      return (
        <LoadingScreen
          sessionId={sessionId}
          payload={loadingPayload}
          onSuccess={handleGenerationSuccess}
          // onGenerate intentionally omitted — LoadingScreen falls back to
          // its local mock until the real UC-02 endpoint exists. Swap in
          // the real call here, e.g. onGenerate={callGenerateDietPlanApi},
          // once it does.
        />
      );

    case SCREENS.DIET_PLAN:
      return (
        <>
          <DietPlanScreen
            sessionId={sessionId}
            plan={dietPlan}
            dislikes={dislikes?.dislikes ?? []}
            // Screen 2 (Basic Information) is the only source of Name/Age/
            // Weight in this wizard — it was being collected into
            // `basicInfo` but never actually threaded down to
            // DietPlanScreen, so its PDF header had nothing to render.
            // `profile` here is intentionally a small, renamed subset
            // (weightKg -> weight) rather than passing `basicInfo`
            // wholesale, since DietPlanScreen/generatePlanPdf only know
            // about `{ name, age, weight, weightUnit }` and don't need
            // gender/heightCm/targetWeightKg.
            profile={{
              name: basicInfo?.name,
              age: basicInfo?.age,
              weight: basicInfo?.weightKg,
              weightUnit: "kg",
            }}
            mealContext={mealContext}
            onEmailPlan={handleOpenEmailPopup}
            // onDownloadPdf/onRegenerateMeal/onReplaceIngredient
            // intentionally omitted — DietPlanScreen falls back to its
            // local mocks until UC-03/UC-04/UC-05's real endpoints exist.
          />
          {showEmailPopup && <EmailPopup sessionId={sessionId} onClose={handleCloseEmailPopup} />}
        </>
      );

    case SCREENS.WELCOME:
    default:
      return <Welcome onNavigateNext={handleWelcomeNext} />;
  }
};

DietPlannerWizard.displayName = "DietPlannerWizard";
export default DietPlannerWizard;